import React from "react"
import { Toggle, Card, CardHeader, CardText, FloatingActionButton, Table, TableBody, TableRow, TableRowColumn } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import strings from "../../localization/strings"
import autobind from "class-autobind"
import * as  accountActions from "../../actions/AccountActions"
import { IHierarchyItemForAccountLevel, IHierarchyItem, IHierarchyItemSettings, IBreak, IJobDocument } from "../../models"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AccountStore from "../../stores/AccountStore"
import AppStateStore from "../../stores/AppStateStore"
import AddBreakDialog from "../../components/break/AddBreakDialog"
import _ from "lodash"
import * as utils from "../../helpers/utils"
import * as mobileBreakHelper from "../../helpers/mobileBreakHelper"
import HierarchyJobVariations from "./hierarchy_job_variations/HierarchyJobVariations"

interface IProps {
    hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument
    inheritedHierarchyJobItem: IHierarchyItem | IJobDocument
    readonly: boolean
    handleUpdateJob(job: IJobDocument): Promise<void>
}
interface IState {
    expand: boolean
    isRoot: boolean
    canOverride: boolean
    mobileBreaks: IBreak[]
    openAddMobileBreakDialog: boolean
    hoveredMobileBreak: IBreak
}

export default class HierarchySettingsMobileBreaks extends React.Component<IProps, IState>  {
    constructor(props) {
        super(props)
        this.state = {
            expand: false,
            isRoot: true,
            canOverride: false,
            mobileBreaks: [],
            openAddMobileBreakDialog: false,
            hoveredMobileBreak: null
        }
        autobind(this)
    }

    componentDidMount() {
        this.checkIsRoot(this.props.hierarchyJobItem)
        this.getMobileBreaks(this.props.inheritedHierarchyJobItem)
    }

    componentWillReceiveProps(nextProps: IProps) {
        !_.isEqual(this.props.hierarchyJobItem, nextProps.hierarchyJobItem) &&
            this.checkIsRoot(nextProps.hierarchyJobItem)
        !_.isEqual(this.props.inheritedHierarchyJobItem, nextProps.inheritedHierarchyJobItem) &&
            this.getMobileBreaks(nextProps.inheritedHierarchyJobItem)
    }

    checkIsRoot(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument) {
        hierarchyJobItem = hierarchyJobItem as IHierarchyItemForAccountLevel
        this.setState({ isRoot: hierarchyJobItem && hierarchyJobItem.level && hierarchyJobItem.level === -1 ? true : false }, () =>
            this.getCanOverride()
        )
    }

    getCanOverride() {
        if (!this.state.isRoot) {
            const hierarchyJobItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
            const inheritedHierarchyJobItemId = this.props.inheritedHierarchyJobItem ? this.props.inheritedHierarchyJobItem.Id : null
            this.setState({ canOverride: hierarchyJobItem.Id === inheritedHierarchyJobItemId })
        }
        else {
            this.setState({ canOverride: false })
        }
    }

    getMobileBreaks(inheritedHierarchyJobItem: (IHierarchyItem | IJobDocument)) {
        const hierarchySettings = inheritedHierarchyJobItem ?
            inheritedHierarchyJobItem.HierarchyItemSettings :
            AccountStore.getCurrentAccount().HierarchyItemSettings
        this.setState({ mobileBreaks: hierarchySettings.MobileBreaks || [] })
    }

    handleSave(canOverride: boolean, mobileBreaks: IBreak[]) {
        this.setState({ canOverride, mobileBreaks }, () =>
            this.state.isRoot ?
                this.updateAccountMobileBreaksSettings() :
                this.updateHierarchyJobItemMobileBreaksSettings()
        )
    }

    updateAccountMobileBreaksSettings() {
        let hierarchyItemSettings = AccountStore.getCurrentAccount().HierarchyItemSettings
        if (hierarchyItemSettings) {
            hierarchyItemSettings.MobileBreaks = this.state.mobileBreaks
        } else {
            hierarchyItemSettings = { MobileBreaks: this.state.mobileBreaks } as IHierarchyItemSettings
        }
        accountActions.patchAccountHierarchySettings(AccountStore.getCurrentAccount().Id, { HierarchyItemSettings: hierarchyItemSettings })
    }

    updateHierarchyJobItemMobileBreaksSettings() {
        const hierarchyJobItem = _.cloneDeep(this.props.hierarchyJobItem) as (IHierarchyItem | IJobDocument)
        if (this.state.canOverride) {
            if (hierarchyJobItem.HierarchyItemSettings) {
                const mobileBreaks = hierarchyJobItem.HierarchyItemSettings.MobileBreaks || []
                this.state.mobileBreaks.forEach(i => {
                    const indexOf = mobileBreaks.findIndex(k => k.Id === i.Id)
                    if (indexOf > -1) {
                        mobileBreaks[indexOf] = i
                    }
                    else {
                        mobileBreaks.push(i)
                    }
                })
                hierarchyJobItem.HierarchyItemSettings.MobileBreaks = mobileBreaks
            } else {
                hierarchyJobItem.HierarchyItemSettings = { MobileBreaks: this.state.mobileBreaks } as IHierarchyItemSettings
            }
        } else {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.MobileBreaks = this.state.mobileBreaks
            } else {
                hierarchyJobItem.HierarchyItemSettings = { MobileBreaks: this.state.mobileBreaks } as IHierarchyItemSettings
            }
        }
        hierarchyJobItem.hasOwnProperty("AccountId") ?
            this.props.handleUpdateJob(hierarchyJobItem as IJobDocument).then(() => AppStateStore.setProgressSnackbarContent(null)) :
            accountActions.updateAccountHierarchyItem(AccountStore.getCurrentAccount().Id, hierarchyJobItem, true)
    }

    render() {
        const editable = this.state.isRoot || this.state.canOverride
        const mobileBreaks = this.state.mobileBreaks.filter(i => !i.Deleted)
        return (
            <div>
                <Card
                    data-intercom-target={"menu-Hierarchy-Settings-MobileBreaks"}
                    expanded={this.state.expand}
                    onExpandChange={expand => this.setState({ expand })}
                >
                    <CardHeader
                        title={strings.Mobile_Breaks}
                        subtitle={[
                            <span key="title">
                                {this.state.isRoot ?
                                    `(${strings.not_inherited})` :
                                    !this.props.inheritedHierarchyJobItem ?
                                        `(${strings.formatString(strings.inherited_from_Account_x, AccountStore.getCurrentAccount().Name)})` :
                                        this.props.inheritedHierarchyJobItem.Id === (this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)).Id ?
                                            `(${strings.not_inherited})` :
                                            `(${strings.formatString(strings.inherited_from_x, `${hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(this.props.inheritedHierarchyJobItem.Id).Label}:  ${(this.props.inheritedHierarchyJobItem as IHierarchyItem).Label}`)})`
                                }
                            </span>,
                            this.state.expand &&
                            <HierarchyJobVariations
                                key="hierarchy job variations"
                                settingPropertyName="MobileBreaks"
                                hierarchyJobItem={this.props.hierarchyJobItem}
                                inheritedHierarchyJobItem={this.props.inheritedHierarchyJobItem}
                            />
                        ]}
                        actAsExpander
                        showExpandableButton
                        titleStyle={{ fontSize: 20, fontWeight: 500, display: "initial" }}
                        subtitleStyle={{ fontSize: 13, color: "rgb(97, 97, 97)", marginLeft: 10, display: "initial" }}
                    />
                    <CardText expandable>
                        <div>
                            {!this.props.readonly && editable &&
                                <FloatingActionButton
                                    data-intercom-target={"menu-Hierarchy-Settings-MobileBreaks-Btn-Add"}
                                    style={{ position: "absolute", marginTop: -93, right: 42, display: "inline-block", zIndex: 100 }}
                                    mini
                                    secondary
                                    onTouchTap={() => this.setState({ openAddMobileBreakDialog: true, hoveredMobileBreak: null })}
                                >
                                    <ContentAdd />
                                </FloatingActionButton>
                            }
                            {!this.state.isRoot &&
                                <Toggle
                                    data-intercom-target={"menu-Hierarchy-Settings-MobileBreaks-Toggle-Override"}
                                    label={strings.Override}
                                    style={{ marginBottom: 20, width: 300 }}
                                    onToggle={(e, canOverride) =>
                                        AppStateStore.setConfirmDialogContent({
                                            content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                            confirm: () => {
                                                let mobileBreaks = _.cloneDeep(this.state.mobileBreaks)
                                                if (canOverride) {
                                                    mobileBreaks = mobileBreaks.filter(i => !i.Deleted)
                                                    mobileBreaks.forEach(i => i.Id = utils.guid())
                                                }
                                                else {
                                                    mobileBreaks.forEach(i => i.Deleted = true)
                                                }
                                                this.handleSave(canOverride, mobileBreaks)
                                            }
                                        })
                                    }
                                    toggled={this.state.canOverride}
                                    disabled={this.props.readonly}
                                />
                            }
                            <div>
                                {!mobileBreaks.length ?
                                    strings.There_is_no_Mobile_Break_item :
                                    <Table
                                        multiSelectable={false}
                                        selectable={false}
                                        fixedHeader={true}
                                        style={{ backgroundColor: "none", borderBottom: "1px solid rgb(224,224,224)", borderTop: "1px solid rgb(224,224,224)" }}
                                        onRowHover={rowNumber =>
                                            this.setState({ hoveredMobileBreak: mobileBreaks[rowNumber] })
                                        }
                                    >
                                        <TableBody showRowHover={editable} deselectOnClickaway={false} displayRowCheckbox={false}>
                                            {mobileBreaks.map((row, i) =>
                                                <TableRow key={i} style={{ heigth: 45 }}>
                                                    <TableRowColumn style={{ textAlign: "left", paddingLeft: 0 }}>
                                                        {`${row.Name} - ${mobileBreakHelper.getMobileBreakTimeInfoString(row)}`}
                                                    </TableRowColumn>
                                                    {!this.props.readonly && editable &&
                                                        <TableRowColumn style={{ width: 40, cursor: "pointer" }} className="prevent-cell-click">
                                                            {this.state.hoveredMobileBreak && this.state.hoveredMobileBreak.Id === row.Id &&
                                                                <div onClick={e => e.stopPropagation()}>
                                                                    <i
                                                                        data-intercom-target={i === 0 && "menu-Hierarchy-Settings-MobileBreakRow-Btn-OpenEditItemDialog"}
                                                                        className="fa fa-pencil"
                                                                        style={{ fontSize: 20, fontWeight: "bold", cursor: "pointer" }}
                                                                        onClick={() => this.setState({ openAddMobileBreakDialog: true })}
                                                                    />
                                                                    <i
                                                                        data-intercom-target={i === 0 && "menu-Hierarchy-Settings-MobileBreakRow-Btn-OpenRemoveItemDialog"}
                                                                        className="fa fa-trash-o"
                                                                        style={{ fontSize: 20, fontWeight: "bold", marginLeft: 10, cursor: "pointer" }}
                                                                        onClick={() =>
                                                                            AppStateStore.setConfirmDialogContent({
                                                                                content: strings.Are_you_sure_you_want_to_delete_the_selected_mobile_break,
                                                                                confirm: () => {
                                                                                    const mobileBreaks = _.cloneDeep(this.state.mobileBreaks)
                                                                                    mobileBreaks.find(i => i.Id === this.state.hoveredMobileBreak.Id).Deleted = true
                                                                                    this.handleSave(this.state.canOverride, mobileBreaks)
                                                                                }
                                                                            })
                                                                        }
                                                                    />
                                                                </div>
                                                            }
                                                        </TableRowColumn>
                                                    }
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                }
                            </div>
                        </div>
                    </CardText>
                </Card >
                {this.state.openAddMobileBreakDialog &&
                    <AddBreakDialog
                        isMobileBreak
                        editBreak={this.state.hoveredMobileBreak}
                        breaks={mobileBreaks}
                        handleClose={() => this.setState({ openAddMobileBreakDialog: false, hoveredMobileBreak: null })}
                        handleAdd={mobileBreak => {
                            const mobileBreaks = [...this.state.mobileBreaks, mobileBreak]
                            this.setState({
                                openAddMobileBreakDialog: false,
                                hoveredMobileBreak: null
                            })
                            this.handleSave(this.state.canOverride, mobileBreaks)
                        }}
                        handleUpdate={mobileBreak => {
                            const { mobileBreaks } = this.state
                            const indexOf = mobileBreaks.findIndex(i => i.Id === mobileBreak.Id)
                            mobileBreaks[indexOf] = mobileBreak
                            this.setState({
                                openAddMobileBreakDialog: false,
                                hoveredMobileBreak: null
                            })
                            this.handleSave(this.state.canOverride, mobileBreaks)
                        }}
                    />
                }
            </div>
        )
    }
}